import { useState } from "react";
import { Button, Card, Checkbox, EmptyState, FormRow, RecordList, Skeleton, Tag, useMinWidth } from "@cntrl/ui";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import { useEpk, useUpdateEpk, type EpkRead } from "../../app/hooks/useEpk.js";
import { useUnsavedSections, saveButtonState } from "./useUnsavedChanges.js";
import { EPK_SECTIONS, toDraft, type Draft, type MomentDraft } from "./epkDraft.js";
import styles from "./EpkCard.module.scss";

const MAX_FFO = 3;
// "Up to three pull-quotes" — the CD Assets spec caps quotes at 3 on both platforms.
const MAX_QUOTES = 3;
const MOMENT_DRAG_KIND = "epk-moment";
const MAX_MOMENTS = 50;

/**
 * Assets → EPK (shots 31-33). One whole-document editor: the share card, band info (tagline / year / based-in /
 * for-fans-of + hide toggle), music embeds (video + Spotify), press quotes, contacts, and a notable-moments table.
 * NO stats-snapshot section (PO ruling 2026-08-16) — stored stat pins still round-trip via the Draft so the
 * whole-document PUT never wipes them. The whole surface is epk:write-gated (NOT assets:write — a non-AV band_member
 * holds assets:write yet is denied EPK). The document persists as a whole (a single PUT), snapshot/restore
 * lives in the shared Brand version machinery (scope="epk"). The public page /epk/:slug is server-rendered.
 */
// ---- shared states --------------------------------------------------------------------------------------------

function LoadingRows() {
  return (
    <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
      <Skeleton variant="line" width="60%" />
      <Skeleton variant="line" width="80%" />
      <Skeleton variant="line" width="45%" />
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load"
      description={error instanceof ApiError ? error.message : "Please try again."}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

export function EpkCard() {
  const q = useEpk();
  // Error/loading branch ONLY on the initial load (no data yet). Once data exists, keep the editor mounted even through
  // a FAILED background refetch (isError=true while last-good data is retained) — else the imperative refetchAllScopes()
  // a WS reconnect fires (which bypasses EDITOR_READ) would unmount the editor and destroy the unsaved draft (the
  // draft-clobber MED). The EPK draft is big and multi-section; this guard protects all of it.
  if (!q.data) {
    return (
      <Card>
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  // NOT keyed on dataUpdatedAt: the draft lives in EpkEditor's local state (initialised once, reset only on an explicit
  // save), so a successful background refetch updates the read-only props WITHOUT clobbering unsaved document edits.
  return <EpkEditor initial={q.data} />;
}

function EpkEditor({ initial }: { initial: EpkRead }) {
  const save = useUpdateEpk();
  const [d, setD] = useState<Draft>(() => toDraft(initial));
  const { unsaved, markSaved } = useUnsavedSections(d, EPK_SECTIONS);

  const publicUrl = absolutePublicUrl(initial.publicPath);
  // ART-187: below `md` the Assets page leads with the tile bar, whose EPK tile already shares this link — so
  // the share hero here is redundant on the phone and is hidden. Desktop (≥ md) has no tile bar, so the hero
  // is the only EPK link and stays.
  const showShareHero = useMinWidth("md");

  const set = (changes: Partial<Draft>) => setD((prev) => ({ ...prev, ...changes }));

  /** Array order IS the order — the server derives `position` from it, so a move is just a splice. One helper
   *  for all three record lists (quotes · contacts · moments), which are all reorderable now. */
  const moveIn = (key: "quotes" | "contacts" | "moments") => (from: number, to: number) =>
    setD((prev) => {
      const list = prev[key] as unknown[];
      if (to < 0 || to >= list.length) return prev;
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...prev, [key]: next };
    });
  const yearInvalid = d.foundedYear.trim() !== "" && !/^\d{4}$/.test(d.foundedYear.trim());

  const onSave = () => {
    if (yearInvalid) return;
    save.mutate(
      {
        tagline: d.tagline.trim() || null,
        foundedYear: d.foundedYear.trim() ? Number(d.foundedYear.trim()) : null,
        basedIn: d.basedIn.trim() || null,
        forFansOf: d.forFansOf.map((f) => f.trim()).filter(Boolean),
        ffoHidden: d.ffoHidden,
        videoEmbedUrl: d.videoEmbedUrl.trim() || null,
        spotifyEmbedUrls: d.spotifyEmbedUrls.map((u) => u.trim()).filter(Boolean),
        quotes: d.quotes
          .map((qq) => ({ quote: qq.quote.trim(), source: qq.source.trim() || null }))
          .filter((qq) => qq.quote),
        // Detail is OPTIONAL, so only a title is required — the old shape also demanded a date and
        // silently dropped any moment without one. Array order IS the order (server derives position).
        moments: d.moments
          .map((m) => ({ title: m.title.trim(), detail: m.detail.trim() || null }))
          .filter((m) => m.title),
        contacts: d.contacts
          .map((c) => ({
            name: c.name.trim(), role: c.role.trim() || null,
            company: c.company.trim() || null, email: c.email.trim() || null,
          }))
          .filter((c) => c.name),
        videoTitle: d.videoTitle.trim() || null,
        statPins: d.statPins,
      },
      {
        // Reset the local draft to the server-normalised result (positions reindexed, blanks dropped) — the deliberate
        // post-save reset now the editor is not remounted by a dataUpdatedAt key.
        onSuccess: (saved) => {
          // The baseline is the SERVER-normalised draft (positions reindexed, blanks dropped), not what was
          // submitted — anything else leaves the card reporting unsaved changes it no longer has.
          const next = toDraft(saved);
          setD(next);
          markSaved(next);
          pushToast("EPK saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save your EPK."), "error"),
      },
    );
  };

  const onCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      pushToast("Public link copied.", "success");
    } catch {
      pushToast("Couldn't copy the link.", "error");
    }
  };

  // One button per section card. They all PUT the whole document (it is one document) and only the section
  // whose own fields moved wears the unsaved fill — see EPK_SECTIONS.
  const saveBtn = (section: keyof typeof EPK_SECTIONS) => (
    <Button {...saveButtonState(unsaved[section], save.isPending || yearInvalid)} size="xs" onClick={onSave}>
      {save.isPending ? "Saving…" : "Save"}
    </Button>
  );

  return (
    <>
      {/* Share bar (CD: the EPK section opens with the stylized dark smart-link card — ink surface,
          crimson glow, display-face headline, pill URL + Copy link / Open). Hidden below `md` (ART-187) —
          the Assets tile bar carries the EPK link there. */}
      {showShareHero && (
        <section className={styles.shareCard} aria-labelledby="epk-share-heading">
          <h3 id="epk-share-heading" className={styles.shareHeadline}>
            One smart-link,
            <br />
            everything press needs.
          </h3>
          <p className={styles.shareBlurb}>Photos, bio, music, quotes and stats, always up to date at one URL.</p>
          {publicUrl && (
            <div className={styles.shareBar}>
              <span className={styles.shareUrl} title={publicUrl}>{publicUrl}</span>
              <div className={styles.shareActions}>
                <button type="button" className={styles.shareBtnSolid} onClick={onCopy}>
                  Copy link
                </button>
                <button type="button" className={styles.shareBtnGhost}
                  onClick={() => window.open(publicUrl, "_blank", "noopener")}>
                  Open
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Artist info · Music · Press quotes · Contact ride a 2-up grid (CD layout); moments + stats full-width. */}
      <div className={styles.cardsGrid}>
      {/* "Artist info", not "Band info" (ART-52) — the Artist IS the band, and this head is the one place
          the press kit still called the tenant something the rest of the product does not. The descriptive
          sub-lines on the EPK cards were killed (ART-203, Ryan). */}
      <Card id="epk" title="Artist info" actions={saveBtn("info")}>

        <FormRow>
          <Field label="Tagline">
            <input className={styles.input} value={d.tagline} placeholder="A one-line hook"
              onChange={(e) => set({ tagline: e.target.value })} />
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Formation year" error={yearInvalid ? "Enter a 4-digit year." : undefined}>
            <input className={styles.input} value={d.foundedYear} placeholder="2015" inputMode="numeric"
              onChange={(e) => set({ foundedYear: e.target.value })} />
          </Field>
          <Field label="Based">
            <input className={styles.input} value={d.basedIn} placeholder="City, State"
              onChange={(e) => set({ basedIn: e.target.value })} />
          </Field>
        </FormRow>

        {/* For fans of */}
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionTitle}>For Fans Of · max {MAX_FFO}</span>
          </div>
          <ForFansOfChips values={d.forFansOf} max={MAX_FFO} onChange={(forFansOf) => set({ forFansOf })} />
          {/* Below the artist-name box, not in the section head (PO, 2026-08-20): the toggle reads as
              part of the control it silences, not as a header ornament. */}
          <Checkbox
            className={styles.toggle}
            checked={d.ffoHidden}
            onChange={(ffoHidden) => set({ ffoHidden })}
            label="Don't show “For Fans Of” on EPK"
          />
        </div>
      </Card>

      {/* Media (CD: video + playlist, both embed on the public EPK; the video also runs in Link in Bio) */}
      <Card id="epk-music" title="Media" actions={saveBtn("media")}>

        <FormRow>
          <Field label="Video URL">
            <input className={styles.input} value={d.videoEmbedUrl} placeholder="Paste a YouTube or Vimeo link"
              inputMode="url" onChange={(e) => set({ videoEmbedUrl: e.target.value })} />
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Video title">
            <input className={styles.input} value={d.videoTitle}
              placeholder="Shows under the player on your EPK and your Link in Bio."
              onChange={(e) => set({ videoTitle: e.target.value })} />
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Spotify playlist">
            <input className={styles.input} value={d.spotifyEmbedUrls[0] ?? ""} placeholder="Paste a Spotify link"
              inputMode="url"
              onChange={(e) => set({ spotifyEmbedUrls: [e.target.value, ...d.spotifyEmbedUrls.slice(1)] })} />
          </Field>
        </FormRow>
        {/* No preview tiles here (Ryan, ART-235 — "don't need these big boxes of nothing"): the real
            video + Spotify embeds render on the public EPK page, so a placeholder box on the editor said
            nothing. */}
      </Card>

      {/* Press quotes */}
      <Card id="epk-quotes" title="Press quotes" actions={saveBtn("quotes")}>
        <RecordList
          dragKind="epk-quote"
          items={d.quotes}
          layout="stack"
          max={MAX_QUOTES}
          showCount
          addLabel="Add a press quote"
          nameOf={(q, i) => (q.source.trim() ? `quote from ${q.source.trim()}` : `quote ${i + 1}`)}
          onAdd={() => set({ quotes: [...d.quotes, { quote: "", source: "" }] })}
          onMove={moveIn("quotes")}
          onRemove={(i) => set({ quotes: d.quotes.filter((_, idx) => idx !== i) })}
          renderFields={(qq, i) => {
            const patch = (changes: Partial<typeof qq>) =>
              set({ quotes: d.quotes.map((x, idx) => (idx === i ? { ...x, ...changes } : x)) });
            return (
              <>
                <textarea className={styles.textarea} rows={2} value={qq.quote} placeholder="“A blistering live act.”"
                  aria-label={`Quote ${i + 1}`} onChange={(e) => patch({ quote: e.target.value })} />
                <input className={styles.input} value={qq.source} placeholder="Source (e.g. Pitchfork)"
                  aria-label={`Quote ${i + 1} source`} onChange={(e) => patch({ source: e.target.value })} />
              </>
            );
          }}
        />
      </Card>

      {/* Contacts — the open list (CD: "add as many people as you need"). */}
      <Card id="epk-contacts" title="Point Of Contact" actions={saveBtn("contacts")}>
        <RecordList
          dragKind="epk-contact"
          items={d.contacts}
          addLabel="Add contact"
          empty="No contacts yet, add your manager, booking agent, or press contact below."
          nameOf={(c, i) => c.name.trim() || `contact ${i + 1}`}
          onAdd={() => set({ contacts: [...d.contacts, { name: "", role: "", company: "", email: "" }] })}
          onMove={moveIn("contacts")}
          onRemove={(i) => set({ contacts: d.contacts.filter((_, idx) => idx !== i) })}
          renderFields={(c, i) => {
            const patch = (changes: Partial<typeof c>) =>
              set({ contacts: d.contacts.map((x, idx) => (idx === i ? { ...x, ...changes } : x)) });
            return (
              <>
                <input className={styles.input} value={c.name} placeholder="Name" aria-label={`Contact ${i + 1} name`}
                  onChange={(e) => patch({ name: e.target.value })} />
                <input className={styles.input} value={c.role} placeholder="Role (Press, Booking…)"
                  aria-label={`Contact ${i + 1} role`} onChange={(e) => patch({ role: e.target.value })} />
                <input className={styles.input} value={c.company} placeholder="Company"
                  aria-label={`Contact ${i + 1} company`} onChange={(e) => patch({ company: e.target.value })} />
                <input className={styles.input} value={c.email} placeholder="Email" inputMode="email"
                  aria-label={`Contact ${i + 1} email`} onChange={(e) => patch({ email: e.target.value })} />
              </>
            );
          }}
        />
      </Card>
      </div>

      {/* Notable moments — moment + optional free-text detail + reorder. No date field: `position`
          is derived server-side from submitted array order, so reordering is just a resubmit. */}
      <Card id="epk-moments" title="Notable moments" subtitle="Career highlights, tours, festivals, collaborations. Shown beside The Story on your public EPK." actions={saveBtn("moments")}>

        <RecordList
          dragKind={MOMENT_DRAG_KIND}
          items={d.moments}
          max={MAX_MOMENTS}
          addLabel="Add another moment"
          nameOf={(m, i) => m.title.trim() || `moment ${i + 1}`}
          onAdd={() => set({ moments: [...d.moments, { title: "", detail: "" }] })}
          onMove={moveIn("moments")}
          onRemove={(i) => set({ moments: d.moments.filter((_, idx) => idx !== i) })}
          renderFields={(m, i) => {
            const patch = (changes: Partial<MomentDraft>) =>
              set({ moments: d.moments.map((x, idx) => (idx === i ? { ...x, ...changes } : x)) });
            return (
              <>
                <input className={styles.input} value={m.title} placeholder="e.g. Toured with Mae"
                  aria-label={`Moment ${i + 1}`} onChange={(e) => patch({ title: e.target.value })} />
                <input className={styles.input} value={m.detail} placeholder="Detail (optional), e.g. Spring 2025 · 14 dates"
                  aria-label={`Moment ${i + 1} detail`} onChange={(e) => patch({ detail: e.target.value })} />
              </>
            );
          }}
        />
      </Card>

    </>
  );
}

// ---- small building blocks ------------------------------------------------------------------------------------

/**
 * "For fans of" as removable chips over one add box, replacing the editable-input-row-per-entry shape.
 *
 * Chips trade inline editing for scanability: to change an entry you remove it and retype. At a cap of three
 * that is cheaper than three full-width inputs, and it matches how the other short taxonomies on this product
 * already read (genres, comparable artists).
 *
 * Blanks cannot arise: the add path ignores an empty string, and the save has always stripped them
 * (`forFansOf: d.forFansOf.map(f => f.trim()).filter(Boolean)`), so loaded data carries none either. The
 * render still skips a blank rather than drawing an empty chip, because a chip you cannot see but can save is
 * worse than one that never appears.
 */
function ForFansOfChips({
  values,
  max,
  onChange,
}: {
  values: string[];
  max: number;
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const atCap = values.filter((v) => v.trim()).length >= max;

  function add() {
    const name = draft.trim();
    setDraft("");
    if (!name) return;
    // Case-insensitive dedupe — "Anberlin" and "anberlin" are one act, and a duplicate would silently
    // burn one of only three slots.
    if (values.some((v) => v.trim().toLowerCase() === name.toLowerCase())) return;
    onChange([...values, name]);
  }

  return (
    <>
      {values.some((v) => v.trim()) && (
        <ul className={styles.chips}>
          {values.map((f, i) =>
            f.trim() ? (
              <li key={`${f}-${i}`}>
                <Tag tone="accent" onRemove={() => onChange(values.filter((_, idx) => idx !== i))}>
                  {f}
                </Tag>
              </li>
            ) : null,
          )}
        </ul>
      )}
      {!atCap && (
        <input
          className={styles.input}
          value={draft}
          placeholder="Artist name — press Enter"
          aria-label="Add a for-fans-of artist"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();   // never submit/save the card on Enter
              add();
            }
          }}
          // Commit on blur too: typing a name and then clicking Save would otherwise discard it silently.
          onBlur={add}
        />
      )}
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {error && <span className={styles.fieldError}>{error}</span>}
    </label>
  );
}

// ---- helpers --------------------------------------------------------------------------------------------------

function absolutePublicUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${path}`;
}

